export default class Accordion {
	#triggers
	#options
	#listeners
	constructor(
		selectorOrElements,
		{ multiple = false, behavior = 'auto', allowCopy = false } = {}
	) {
		if (!selectorOrElements)
			throw new Error('Selector or Dom elements is required')

		this.#triggers =
			typeof selectorOrElements === 'string'
				? [...document.querySelectorAll(selectorOrElements)]
				: selectorOrElements

		this.#options = {
			multiple,
			behavior,
			allowCopy,
		}

		this.#listeners = {
			click: this.#clickHandler.bind(this),
		}

		this.#triggers.forEach(trigger =>
			trigger.addEventListener('click', this.#listeners.click)
		)

		if (this.#options.behavior === 'smooth') {
			this.#triggers.forEach(trigger => {
				const target = this.#getTarget(trigger)

				target.style.height = target.scrollHeight + 'px'
				target.style.overflow = 'hidden'
				target.style.transition = 'all 0.5s'
			})
		}

		if (!this.#options.multiple) this.#hideOther(null)
	}

	#toggleItem(trigger) {
		const target = this.#getTarget(trigger)

		trigger.classList.toggle('active')
		target.classList.toggle('active')

		if (this.#options.behavior === 'smooth') {
			target.style.height = target.classList.contains('active')
				? target.scrollHeight + 'px'
				: 0
		}
	}

	#getTarget(trigger) {
		const hasDataTarget = trigger.hasAttribute('data-target')

		return hasDataTarget
			? this.#getTargetByDataset(trigger)
			: this.#getTargetByTriggerSibling(trigger)
	}

	#getTargetByDataset(trigger) {
		// data-target - це CSS-селектор дочірнього елемента всередині trigger.
		// Пошук відбувається виключно в межах trigger через querySelector.
		const datasetSelector = trigger.dataset.target

		if (datasetSelector.trim() === '')
			throw new Error('Dataset attribute is empty string')

		const targetElement = trigger.querySelector(datasetSelector)
		if (!targetElement)
			throw new Error(
				`Cannot find accordion target via dataset in trigger: ${trigger.outerHTML}`
			)

		return targetElement
	}

	#getTargetByTriggerSibling(trigger) {
		const triggerSibling = trigger.nextElementSibling
		if (!triggerSibling)
			throw new Error(
				`Cannot find accordion target via sibling in trigger: ${trigger.outerHTML}`
			)

		return triggerSibling
	}

	#clickHandler(event) {
		if (this.#options.allowCopy && window.getSelection().toString()) {
			event.stopImmediatePropagation()
			return
		}

		const currentTrigger = event.currentTarget

		if (!this.#options.multiple) this.#hideOther(currentTrigger)

		this.#toggleItem(currentTrigger)
	}

	#hideOther(currentTrigger) {
		this.#triggers.forEach(trigger => {
			if (currentTrigger === trigger) return

			const target = this.#getTarget(trigger)

			trigger.classList.remove('active')
			target.classList.remove('active')

			if (this.#options.behavior === 'smooth') target.style.height = 0
		})
	}

	#validateIndex(ind, arr) {
		if (typeof ind !== 'number')
			throw new Error(`Fail to open accordion target. ${ind} is not a number`)

		if (ind >= arr.length || ind < 0)
			throw new Error(
				`Fail to open accordion target. Cannot find element with index ${ind}`
			)
	}

	closeAll() {
		this.#hideOther(null)
	}

	open(index) {
		this.#validateIndex(index, this.#triggers)

		const trigger = this.#triggers[index]

		if (trigger.classList.contains('active')) return

		if (!this.#options.multiple) this.#hideOther(null)

		this.#toggleItem(trigger)
	}

	destroy() {
		this.#triggers.forEach(trigger =>
			trigger.removeEventListener('click', this.#listeners.click)
		)
	}
}
